import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import { useTmaisUsers } from '@/common/hooks/queries/useTmaisUsers'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { Option } from '@/common/types/option'
import { useEstablishmentCompanies } from '@/features/establishments/hooks/useEstablishmentCompanies'
import { EstablishmentCompanyTransformer } from '@/features/establishments/services/establishmentCompanyTransformer'
import { tmaisNfcesColumns } from '@/features/tmaisNfces/constants/columns.constant'
import { tmaisNfcesStatusOptions } from '@/features/tmaisNfces/constants/statusOptions.constant'
import { useTmaisNfces } from '@/features/tmaisNfces/hooks/useTmaisNfces'
import { TmaisNfcesTransformer } from '@/features/tmaisNfces/services/tmaisNfcesTransformer'
import { TmaisNfceData, TmaisNfceTableItem } from '@/features/tmaisNfces/types/tmaisNfces.type'
import { ScanLine, SearchIcon } from 'lucide-react'

const TmaisNfcesList = () => {
  const { filterParams, setFilterParams } = useFilterParams()
  const [search, setSearch] = useDebounce(filterParams.search || '')
  const [searchEstablishment, setSearchEstablishment] = useDebounce('')
  const [searchUploadedBy, setSearchUploadedBy] = useDebounce('')

  const { date, formattedDate } = useFilterDate(filterParams)

  const { data: establishmentOptions } = useEstablishmentCompanies({
    filters: {
      search: searchEstablishment,
      tmaisModuleEnabled: true,
    },
    mapper: EstablishmentCompanyTransformer.optionTransformer({ valueFieldName: 'id' }),
  })

  const { data: tmaisUserOptions } = useTmaisUsers<Option[]>({
    filters: {
      search: searchUploadedBy,
    },
    mapper: Mapper.mapToOptions({ labelFieldName: 'full_name', valueFieldName: 'id' }),
  })

  const {
    data: nfces = [],
    meta,
    isPending,
    error: nfcesError = {} as any,
    isFetching,
    refetch,
  } = useTmaisNfces<TmaisNfceTableItem[]>({
    filters: {
      search,
      page: filterParams.page || 1,
      status: filterParams.status?.value,
      uploadedById: filterParams.uploadedBy?.value,
      establishmentCompanyId: filterParams.establishment?.value,
      createdAtGteq: formattedDate.from,
      createdAtLteq: formattedDate.to,
    },
    mapper: Mapper.mapWithTransformer<TmaisNfceData[], TmaisNfceTableItem[]>({
      transformer: TmaisNfcesTransformer.tableTransformer(),
    }),
  })

  return (
    <div className="flex flex-col gap-6">
      <ListHeader Icon={<ScanLine className="size-10" strokeWidth={1.5} />} title="NFCe Escaneadas" />

      <ErrorMessageWrapper className="h-[calc(100vh-128px)]" statusCode={nfcesError?.status} refetchFunction={refetch}>
        <div className="h-[calc(100vh-128px)] overflow-auto flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <TextField
              LeftIcon={<SearchIcon />}
              placeholder="Pesquisar"
              className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
              onChange={(e) => {
                const value = (e.target as HTMLInputElement).value
                setSearch(value)
                setFilterParams((prev) => ({ ...prev, search: value, page: 1 }))
              }}
              value={filterParams.search || ''}
            />

            <div className="flex items-center gap-2">
              <DatePickerWithRange
                placeholder="Data de processamento"
                date={date}
                setDate={(date) => setFilterParams((prev) => ({ ...prev, date }))}
              />

              <ListFilters
                filters={[
                  {
                    className: 'min-w-40',
                    onChange: (e: any) => setFilterParams((params) => ({ ...params, status: e?.value ? e : null })),
                    value: filterParams?.status || '',
                    options: [{ label: 'Todos', value: '' }, ...tmaisNfcesStatusOptions],
                    placeholder: 'Status',
                    dataTestid: 'filter_status',
                  },
                  {
                    className: 'min-w-40',
                    onChange: (e: any) => setFilterParams((params) => ({ ...params, uploadedBy: e?.value ? e : null })),
                    onInputChange: setSearchUploadedBy,
                    value: filterParams?.uploadedBy || '',
                    options: [{ label: 'Todos', value: '' }, ...(tmaisUserOptions as Option[])],
                    placeholder: 'Processada por',
                    dataTestid: 'filter_uploaded_by',
                  },
                  {
                    className: 'min-w-40',
                    onChange: (e: any) =>
                      setFilterParams((params) => ({ ...params, establishment: e?.value ? e : null })),
                    onInputChange: setSearchEstablishment,
                    value: filterParams?.establishment || '',
                    options: [
                      { label: 'Todos', value: '' },
                      { label: 'Taurus (Global)', value: 'null' },
                      ...(establishmentOptions as Option[]),
                    ],
                    placeholder: 'Estabelecimento',
                    dataTestid: 'filter_establishment',
                  },
                ]}
              />
            </div>
          </div>

          <Table
            columns={tmaisNfcesColumns}
            rows={nfces || []}
            isLoadingData={isPending || isFetching}
            page={filterParams.page || 1}
            totalPages={meta?.total_pages || 1}
            totalCount={meta?.total || 0}
            hasPagination
            setPage={(page) => setFilterParams((prev: any) => ({ ...prev, page }))}
            rowEmptyValue="--"
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default TmaisNfcesList
